import React from 'react';

import {TranslatedOfficeLocation} from '@lexian/api/casus-api';
import {useMasterdata} from '_/hooks/useMasterdata';
import i18n from '_/i18n';
import {EFAutocompleteProps} from '_/utilities/form';
import {getOptions} from '_/utilities/masterdata.utils';
import {assertUnreachable} from '_/utilities/types';

import {ActiveState} from './ActiveFilter/ActiveFilter.types';
import {
  ActiveStateFilterState,
  useActiveStateFilter,
} from './ActiveFilter/useActiveStateFilter';
import {
  AutocompleteBase,
  staticLoader,
} from './AutocompleteBase/AutocompleteBase';

const filterByActiveState = (
  officeLocations: TranslatedOfficeLocation[],
  activeState: ActiveState
): TranslatedOfficeLocation[] => {
  switch (activeState) {
    case 'ALL':
      return officeLocations;
    case 'ACTIVE':
      return officeLocations.filter(({active}) => active);
    case 'INACTIVE':
      return officeLocations.filter(({active}) => !active);
    default:
      return assertUnreachable(activeState);
  }
};

type Props = Omit<EFAutocompleteProps, 'options'> & {
  filterable?: boolean;
  persistenceKey?: string;
  onUserChange?: (officeLocation: TranslatedOfficeLocation | undefined) => void;
};

export function OfficeLocationAutocomplete({
  label = i18n.t('common:labels.officeLocation'),
  onUserChange,
  filterable = false,
  persistenceKey = 'office-location-autocomplete',
  ...rest
}: Props): React.ReactElement {
  const {officeLocations} = useMasterdata();
  const activeStateFilter = useActiveStateFilter(
    `active-filter.officeLocationAutocomplete.searchFilters.${persistenceKey}`
  );

  return (
    <AutocompleteBase
      {...rest}
      filter={filterable ? activeStateFilter : undefined}
      label={label}
      loadItems={staticLoader(
        (filterState: ActiveStateFilterState | undefined) =>
          filterByActiveState(
            officeLocations,
            filterState?.activeState ?? 'ACTIVE'
          )
      )}
      reloadTrigger={officeLocations}
      toOption={(officeLocation) => getOptions([officeLocation])[0]}
      noOptionFiltering
      onItemChange={onUserChange}
    />
  );
}
